import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Divider,
  InputAdornment
} from '@mui/material';
import {
  Email as EmailIcon,
  VpnKey as VpnKeyIcon,
  ArrowBack as ArrowBackIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebase';

export const PasswordReset = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    type: 'info',
    message: ''
  });

  // Handle email input change
  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    
    // Clear alert when user starts typing
    if (alert.show) {
      setAlert({ show: false, type: 'info', message: '' });
    }
  };

  // Validate email format
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validation
    if (!email.trim()) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Bitte geben Sie Ihre E-Mail-Adresse ein.'
      });
      return;
    }

    if (!validateEmail(email)) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.'
      });
      return;
    }

    setLoading(true);

    try {
      // Send password reset email using Firebase
      await sendPasswordResetEmail(auth, email);
      
      console.log('Password reset email sent to:', email);
      
      setAlert({
        show: true,
        type: 'success',
        message: `Ein Link zum Zurücksetzen des Passworts wurde an ${email} gesendet. Überprüfen Sie Ihr E-Mail-Postfach und folgen Sie den Anweisungen.`
      });
      
      // Clear email field after successful send
      setEmail('');
      
    } catch (error) {
      console.error('Error sending password reset email:', error);
      
      // Handle specific Firebase errors
      let errorMessage = 'Fehler beim Senden der E-Mail';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'Kein Benutzer mit dieser E-Mail-Adresse gefunden. Überprüfen Sie Ihre E-Mail-Adresse oder registrieren Sie sich.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Ungültige E-Mail-Adresse.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Netzwerkfehler. Überprüfen Sie Ihre Internetverbindung.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Zu viele Anfragen. Bitte warten Sie einen Moment und versuchen Sie es erneut.';
          break;
        default:
          errorMessage = `Fehler beim Senden der E-Mail: ${error.message}`;
      }
      
      setAlert({
        show: true,
        type: 'error',
        message: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setEmail('');
    setAlert({
      show: false,
      type: 'info',
      message: ''
    });
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <VpnKeyIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" gutterBottom>
            Passwort zurücksetzen
          </Typography>
        </Box>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Geben Sie Ihre E-Mail-Adresse ein, um einen Link zum Zurücksetzen Ihres Passworts zu erhalten.
        </Typography>
        
        <Divider sx={{ mb: 3 }} />

        <Box component="form" onSubmit={handleSubmit}>
          {/* Email Input */}
          <TextField
            fullWidth
            label="E-Mail-Adresse"
            type="email"
            value={email}
            onChange={handleEmailChange}
            margin="normal"
            required
            variant="outlined"
            helperText="Geben Sie die E-Mail-Adresse Ihres Kontos ein"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              )
            }}
          />

          {/* Information Box */}
          <Box 
            sx={{ 
              mt: 3, 
              p: 2, 
              backgroundColor: 'info.main', 
              color: 'info.contrastText',
              borderRadius: 1
            }}
          >
            <Typography variant="body2">
              <strong>Hinweis:</strong> Nach dem Absenden erhalten Sie eine E-Mail mit einem Link zum Zurücksetzen 
              Ihres Passworts. Der Link ist für eine begrenzte Zeit gültig.
            </Typography>
          </Box>

          {/* Alert above submit button */}
          {alert.show && (
            <Alert severity={alert.type} sx={{ mt: 3, mb: 2 }}>
              {alert.message}
            </Alert>
          )}

          {/* Submit buttons */}
          <Box sx={{ mt: 4, display: 'flex', gap: 2, flexDirection: 'column' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={<SendIcon />}
              size="large"
              fullWidth
            >
              {loading ? 'E-Mail wird gesendet...' : 'Passwort-Reset E-Mail senden'}
            </Button>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={handleReset}
                disabled={loading}
                sx={{ flex: 1 }}
              >
                Zurücksetzen
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/')}
                disabled={loading}
                sx={{ flex: 1 }}
              >
                Zurück zum Login
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default PasswordReset;
